<template>
  <v-app>
    <v-main>
      <v-container fluid class="pa-4">
        <!-- 头部 -->
        <v-row class="mb-4 align-center">
          <v-col cols="12">
            <v-card flat>
              <v-card-text class="pa-0">
                <div class="d-flex align-center">
                  <v-icon large color="primary" class="mr-2">mdi-monitor-dashboard</v-icon>
                  <h2 class="headline font-weight-bold">模型测试监控面板</h2>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- 厂商筛选 -->
        <v-row class="mb-3">
          <v-col cols="12">
            <v-card>
              <v-card-text>
                <div class="d-flex align-center flex-wrap">
                  <div class="mr-4 text-subtitle-1">选择厂商：</div>
                  <v-radio-group
                      v-model="selectedProvider"
                      @change="loadData"
                      row
                      dense
                      class="mt-0"
                  >
                    <v-radio label="全部" value=""></v-radio>
                    <v-radio
                        v-for="p in providers"
                        :key="p"
                        :label="p"
                        :value="p"
                        class="mr-2"
                    ></v-radio>
                  </v-radio-group>
                  <v-btn
                      text
                      color="primary"
                      @click="loadProviders"
                      class="ml-4"
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    刷新
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- 统计卡片 -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card">
              <div class="stat-number info-text">{{stats.totalModels}}</div>
              <div class="stat-label">总模型数</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card">
              <div class="stat-number success-text">{{stats.successCount}}</div>
              <div class="stat-label">成功</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card">
              <div class="stat-number danger-text">{{stats.failedCount}}</div>
              <div class="stat-label">失败</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card">
              <div class="stat-number warning-text">{{stats.avgLatency}} ms</div>
              <div class="stat-label">平均延迟</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- 图表区域 -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>状态分布</v-card-title>
              <v-card-text>
                <div id="statusChart" class="chart-container"></div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>厂商对比</v-card-title>
              <v-card-text>
                <div id="providerChart" class="chart-container"></div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- 延迟分布图 -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card>
              <v-card-title>模型延迟分布 (ms)</v-card-title>
              <v-card-text>
                <div id="latencyChart" class="latency-chart"></div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- 测试结果表格 -->
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <div class="d-flex align-baseline justify-center flex-wrap" style="width: 100%; gap: 12px;">
                  <span>测试结果详情</span>
                  <v-select
                      v-model="selectedBatch"
                      :items="batchItems"
                      placeholder="选择批次"
                      dense
                      outlined
                      style="max-width: 280px;"
                      clearable
                      @change="loadData"
                  ></v-select>
                  <v-select
                      v-model="selectedStatus"
                      :items="statusItems"
                      placeholder="状态筛选"
                      dense
                      outlined
                      style="max-width: 150px;"
                      clearable
                  ></v-select>
                  <v-btn
                      color="success"
                      small
                      @click="exportExcel"
                      class="ml-auto"
                  >
                    <v-icon left>mdi-download</v-icon>
                    导出 Excel
                  </v-btn>
                  <span style="color: rgba(0, 0, 0, 0.6); font-size: 13px;">
                    共 {{filteredTableData.length}} 条记录
                  </span>
                </div>
              </v-card-title>
              <v-card-text>
                <v-data-table
                    :headers="headers"
                    :items="filteredTableData"
                    :loading="loading"
                    :items-per-page="10"
                    class="elevation-1"
                >
                  <template v-slot:item.providerName="{ item }">
                    <v-chip small color="primary">
                      {{item.providerName || 'default'}}
                    </v-chip>
                  </template>
                  <template v-slot:item.modelId="{ item }">
                    <div class="model-id-cell" :title="item.modelId">
                      {{item.modelId}}
                    </div>
                  </template>
                  <template v-slot:item.status="{ item }">
                    <v-chip
                        :color="getStatusColor(item.status)"
                        small
                        dark
                    >
                      {{getStatusText(item.status)}}
                    </v-chip>
                  </template>
                  <template v-slot:item.latency="{ item }">
                    <span :class="getLatencyClass(item.latency)">
                      {{item.latency}}
                    </span>
                  </template>
                  <template v-slot:item.errorMsg="{ item }">
                    <div style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                         :title="item.errorMsg">
                      {{item.errorMsg}}
                    </div>
                  </template>
                  <template v-slot:item.testTime="{ item }">
                    {{formatTime(item.testTime)}}
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- 刷新按钮 -->
      <v-btn
          class="refresh-btn"
          color="primary"
          fab
          @click="loadData"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>

      <!-- 消息提示 -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="3000"
        top
        right
      >
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn
            icon
            v-bind="attrs"
            @click="snackbar.show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<script>
import { speakApi } from '@/api/resources/speak'; // 引入适配后的API模块
import * as echarts from 'echarts';

export default {
  name: 'ModelTestMonitor',
  data: () => ({
    loading: false,
    running: false,
    selectedProvider: '',
    selectedBatch: '',
    selectedStatus: '',
    providers: [],
    batches: [],
    tableData: [],
    stats: {
      totalModels: 0,
      successCount: 0,
      failedCount: 0,
      avgLatency: 0
    },
    statusChart: null,
    providerChart: null,
    latencyChart: null,
    headers: [
      { text: '厂商', value: 'providerName', width: '120px' },
      { text: '模型ID', value: 'modelId', width: '200px' },
      { text: '状态', value: 'status', width: '120px' },
      { text: '延迟(ms)', value: 'latency', width: '120px', sortable: true },
      { text: '错误信息', value: 'errorMsg', width: '200px' },
      { text: '测试时间', value: 'testTime', width: '180px' }
    ],
    snackbar: {
      show: false,
      text: '',
      color: 'info'
    }
  }),
  computed: {
    filteredTableData() {
      if (!this.selectedStatus) {
        return this.tableData;
      }
      return this.tableData.filter(item => item.status === this.selectedStatus);
    },
    batchItems() {
      const items = [{ text: '最新批次', value: '' }];
      this.batches.forEach(item => {
        items.push({
          text: `${item.providerName} - ${item.testTime}`,
          value: item.batchId
        });
      });
      return items;
    },
    statusItems() {
      return [
        { text: '全部状态', value: '' },
        { text: '成功', value: 'SUCCESS' },
        { text: '失败', value: 'FAILED' },
        { text: '错误', value: 'ERROR' },
        { text: '格式异常', value: 'FORMAT_ERROR' }
      ];
    }
  },
  mounted() {
    this.initCharts();
    this.loadProviders();
    this.loadBatches();
    this.loadData();
  },
  methods: {
    initCharts() {
      this.$nextTick(() => {
        this.statusChart = echarts.init(document.getElementById('statusChart'));
        this.providerChart = echarts.init(document.getElementById('providerChart'));
        this.latencyChart = echarts.init(document.getElementById('latencyChart'));

        window.addEventListener('resize', () => {
          this.statusChart.resize();
          this.providerChart.resize();
          this.latencyChart.resize();
        });
      });
    },

    async loadProviders() {
      try {
        const res = await speakApi.getBatches();
        if (res.data.success) {
          const providerSet = new Set();
          res.data.data.forEach(item => {
            if (item.providerName) providerSet.add(item.providerName);
          });
          this.providers = Array.from(providerSet);
        }
      } catch (error) {
        console.error('加载厂商失败:', error);
        this.showMessage('加载厂商失败', 'error');
      }
    },

    async loadBatches() {
      try {
        const res = await speakApi.getBatches();
        if (res.data.success) {
          this.batches = res.data.data.map(item => ({
            ...item,
            testTime: this.formatTime(item.testTime)
          }));
        }
      } catch (error) {
        console.error('加载批次失败:', error);
        this.showMessage('加载批次失败', 'error');
      }
    },

    async loadData() {
      this.loading = true;
      try {
        const params = {};
        if (this.selectedProvider) params.providerName = this.selectedProvider;
        if (this.selectedBatch) params.batchId = this.selectedBatch;

        const res = await speakApi.getHistory(params);
        if (res.data.success) {
          this.tableData = res.data.data;
          this.calculateStats();
          this.updateCharts();
        }
      } catch (error) {
        console.error('加载数据失败:', error);
        this.showMessage('加载数据失败', 'error');
      } finally {
        this.loading = false;
      }
    },

    calculateStats() {
      const data = this.tableData;
      this.stats.totalModels = data.length;
      this.stats.successCount = data.filter(d => d.status === 'SUCCESS').length;
      this.stats.failedCount = data.filter(d => d.status !== 'SUCCESS').length;

      const latencies = data.filter(d => d.latency).map(d => d.latency);
      this.stats.avgLatency = latencies.length > 0
          ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length)
          : 0;
    },

    updateCharts() {
      this.updateStatusChart();
      this.updateProviderChart();
      this.updateLatencyChart();
    },

    updateStatusChart() {
      const statusCount = {};
      this.tableData.forEach(item => {
        statusCount[item.status] = (statusCount[item.status] || 0) + 1;
      });

      const statusColors = {
        'SUCCESS': '#4CAF50',
        'FAILED': '#F44336',
        'ERROR': '#FF9800',
        'FORMAT_ERROR': '#9E9E9E'
      };

      const data = Object.keys(statusCount).map(key => ({
        name: this.getStatusText(key),
        value: statusCount[key],
        itemStyle: { color: statusColors[key] || '#2196F3' }
      }));

      this.statusChart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { orient: 'vertical', left: 'left' },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          label: { show: true, formatter: '{b}: {c}' },
          data: data
        }]
      });
    },

    updateProviderChart() {
      const providerStats = {};
      this.tableData.forEach(item => {
        const provider = item.providerName || 'default';
        if (!providerStats[provider]) {
          providerStats[provider] = { success: 0, failed: 0 };
        }
        if (item.status === 'SUCCESS') {
          providerStats[provider].success++;
        } else {
          providerStats[provider].failed++;
        }
      });

      const providers = Object.keys(providerStats);
      const successData = providers.map(p => providerStats[p].success);
      const failedData = providers.map(p => providerStats[p].failed);

      this.providerChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['成功', '失败'] },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: providers },
        yAxis: { type: 'value' },
        series: [
          { name: '成功', type: 'bar', stack: 'total', data: successData, itemStyle: { color: '#4CAF50' } },
          { name: '失败', type: 'bar', stack: 'total', data: failedData, itemStyle: { color: '#F44336' } }
        ]
      });
    },

    updateLatencyChart() {
      const data = this.tableData
          .filter(item => item.latency && item.status === 'SUCCESS')
          .sort((a, b) => b.latency - a.latency)
          .slice(0, 30);

      this.latencyChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
        xAxis: {
          type: 'category',
          data: data.map(d => d.modelId.length > 20 ? d.modelId.substring(0, 20) + '...' : d.modelId),
          axisLabel: { rotate: 45, interval: 0 }
        },
        yAxis: { type: 'value', name: 'ms' },
        series: [{
          type: 'bar',
          data: data.map(d => ({
            value: d.latency,
            itemStyle: {
              color: d.latency < 1000 ? '#4CAF50' : (d.latency < 3000 ? '#FF9800' : '#F44336')
            }
          }))
        }]
      });
    },

    async runAllTest() {
      this.running = true;

      try {
        const res = await speakApi.runAllTests();
        if (res.data.success) {
          this.showMessage('测试完成！', 'success');
          this.loadBatches();
          this.loadData();
        } else {
          this.showMessage(res.data.message || '测试失败', 'error');
        }
      } catch (error) {
        console.error('测试请求失败:', error);
        this.showMessage('测试请求失败', 'error');
      } finally {
        this.running = false;
      }
    },

    getStatusColor(status) {
      const colors = {
        'SUCCESS': 'success',
        'FAILED': 'error',
        'ERROR': 'warning',
        'FORMAT_ERROR': 'grey'
      };
      return colors[status] || 'info';
    },

    getStatusText(status) {
      const texts = {
        'SUCCESS': '成功',
        'FAILED': '失败',
        'ERROR': '错误',
        'FORMAT_ERROR': '格式异常'
      };
      return texts[status] || status;
    },

    getLatencyClass(latency) {
      if (!latency) return '';
      if (latency < 1000) return 'success-text';
      if (latency < 3000) return 'warning-text';
      return 'danger-text';
    },

    formatTime(time) {
      if (!time) return '';
      const date = new Date(time);
      return date.toLocaleString('zh-CN');
    },

    exportExcel() {
      let url = '/page/model-test/export?';
      if (this.selectedProvider) url += 'providerName=' + encodeURIComponent(this.selectedProvider) + '&';
      if (this.selectedBatch) url += 'batchId=' + encodeURIComponent(this.selectedBatch);

      window.open(url, '_blank');
      this.showMessage('正在导出...', 'info');
    },

    showMessage(text, color = 'info') {
      this.snackbar = {
        show: true,
        text,
        color
      };
    }
  }
};
</script>

<style scoped>
.stat-card {
  text-align: center;
  padding: 20px;
  height: 100%;
}
.stat-number {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
}
.stat-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}
.chart-container {
  height: 300px;
  width: 100%;
}
.latency-chart {
  height: 400px;
  width: 100%;
}
.refresh-btn {
  position: fixed;
  right: 30px;
  bottom: 30px;
  z-index: 999;
}
.success-text { color: #4CAF50 !important; }
.danger-text { color: #F44336 !important; }
.warning-text { color: #FF9800 !important; }
.info-text { color: #9E9E9E !important; }
.v-data-table__wrapper {
  max-height: 500px;
  overflow-y: auto;
}
.model-id-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
